import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft, faSearch, faSyncAlt, faBuilding,
  faCheckCircle, faTimesCircle, faInfoCircle, faHistory,
  faMoneyBillTransfer, faUser, faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../components/shared/DataTable';
import { UiPageHeader, UiButton, UiModal, UiInput, StatusBadge } from '../components/ui';
import type { StatusDef } from '../components/ui';

const TRANSFER_STATUS_MAP: Record<string, StatusDef> = {
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: faCheckCircle, label: 'สำเร็จ' },
  reversed:  { bg: 'bg-amber-100', text: 'text-amber-700', icon: faRotateLeft, label: 'reversed' },
  failed:    { bg: 'bg-red-100', text: 'text-red-600', icon: faTimesCircle, label: 'failed' },
};

interface TransferableMerchant {
  id: string;
  name: string;
  company_name: string;
  balance: number;
  is_frozen: boolean;
}

interface Transfer {
  id: string;
  reference_no: string;
  from_merchant_id: string;
  to_merchant_id: string;
  amount: number;
  description: string | null;
  status: 'completed' | 'failed' | 'reversed';
  from_balance_before: number;
  from_balance_after: number;
  to_balance_before: number;
  to_balance_after: number;
  created_by_name: string;
  created_at: string;
  from_merchant: { id: string; name: string; company_name: string } | null;
  to_merchant: { id: string; name: string; company_name: string } | null;
}

interface PreviewData {
  from_merchant: { id: string; name: string; company_name: string };
  to_merchant:   { id: string; name: string; company_name: string };
  amount: number;
  from_balance_before: number;
  from_balance_after: number;
  to_balance_before: number;
  to_balance_after: number;
  from_wallet_frozen: boolean;
  to_wallet_frozen: boolean;
  sufficient_balance: boolean;
}

const fmt = (n: number) =>
  n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CompanyTransferPage: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'transfer' | 'history'>('transfer');

  // Transfer form
  const [myBalance, setMyBalance] = useState<number | null>(null);
  const [myName, setMyName] = useState('');
  const [merchants, setMerchants] = useState<TransferableMerchant[]>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(true);
  const [toMerchant, setToMerchant] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // History
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 30, offset: 0 });

  useEffect(() => {
    loadMyWallet();
    loadMerchants();
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory(0);
  }, [tab]);

  const loadMyWallet = async () => {
    try {
      const res = await merchantApi.get('/merchants/wallet');
      setMyBalance(parseFloat(res.data.data?.balance || 0));
      setMyName(res.data.data?.merchant?.name || '');
    } catch {
      // silent
    }
  };

  const loadMerchants = async () => {
    try {
      setLoadingMerchants(true);
      const res = await merchantApi.get('/merchants/wallet/company-transfer/merchants');
      setMerchants(res.data.data || []);
    } catch {
      toast.error(t('companyTransfer.cannotLoadMerchants'));
    } finally {
      setLoadingMerchants(false);
    }
  };

  const loadHistory = useCallback(async (offset = 0) => {
    try {
      setHistLoading(true);
      const res = await merchantApi.get(`/merchants/wallet/company-transfer/history?limit=30&offset=${offset}`);
      setTransfers(res.data.data || []);
      setPagination(p => ({ ...p, total: res.data.pagination?.total || 0, offset }));
    } catch {
      toast.error(t('companyTransfer.cannotLoadHistory'));
    } finally {
      setHistLoading(false);
    }
  }, []);

  const handlePreview = async () => {
    if (!toMerchant || !amount || parseFloat(amount) <= 0) {
      toast.warning(t('companyTransfer.pleaseSelectMerchant'));
      return;
    }
    try {
      setPreviewing(true);
      const res = await merchantApi.post('/merchants/wallet/company-transfer/preview', {
        to_merchant_id: toMerchant,
        amount: parseFloat(amount),
      });
      setPreview(res.data.data);
      setShowConfirm(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('common.error'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      const res = await merchantApi.post('/merchants/wallet/company-transfer', {
        to_merchant_id: toMerchant,
        amount: parseFloat(amount),
        description: description || null,
      });
      toast.success(res.data.message);
      setShowConfirm(false);
      setToMerchant('');
      setAmount('');
      setDescription('');
      setPreview(null);
      loadMyWallet();
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('companyTransfer.transferFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const insufficient = myBalance !== null && !!amount && parseFloat(amount) > myBalance;

  const historyColumns: Column<Transfer>[] = [
    {
      key: 'reference_no',
      header: 'Ref',
      render: (tx) => (
        <span className="font-mono text-xs font-bold px-2 py-0.5 rounded"
          style={{ color: 'var(--theme-primary)', background: 'var(--theme-primary-light)' }}>
          {tx.reference_no}
        </span>
      ),
    },
    {
      key: 'route',
      header: t('companyTransfer.tabTransfer'),
      render: (tx) => (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700 dark:text-slate-200">{tx.from_merchant?.name || '-'}</span>
          <FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-xs" style={{ color: 'var(--theme-primary)' }} />
          <span className="font-medium text-gray-700 dark:text-slate-200">{tx.to_merchant?.name || '-'}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (tx) => <StatusBadge status={tx.status} statusMap={TRANSFER_STATUS_MAP} />,
    },
    {
      key: 'amount',
      header: t('common.amount'),
      sortable: true,
      render: (tx) => <span className="text-sm font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmt(tx.amount)}</span>,
    },
    {
      key: 'balance',
      header: t('wallet.balanceChange'),
      render: (tx) => (
        <div className="text-xs text-gray-500 dark:text-slate-400 space-y-0.5">
          <div className="truncate">{tx.from_merchant?.name}: ฿{fmt(tx.from_balance_before)} <FontAwesomeIcon icon={faArrowRightArrowLeft} className="mx-0.5 text-[8px]" /> <span className="text-red-600 dark:text-red-400 font-medium">฿{fmt(tx.from_balance_after)}</span></div>
          <div className="truncate">{tx.to_merchant?.name}: ฿{fmt(tx.to_balance_before)} <FontAwesomeIcon icon={faArrowRightArrowLeft} className="mx-0.5 text-[8px]" /> <span className="text-emerald-600 dark:text-emerald-400 font-medium">฿{fmt(tx.to_balance_after)}</span></div>
        </div>
      ),
    },
    {
      key: 'meta',
      header: t('common.date'),
      render: (tx) => (
        <div className="text-xs text-gray-400 dark:text-slate-500">
          <div>{new Date(tx.created_at).toLocaleString('th-TH')}</div>
          <div className="mt-0.5"><FontAwesomeIcon icon={faUser} className="mr-1" />{tx.created_by_name}</div>
        </div>
      ),
    },
  ];

  const MERCHANT_STATUS_MAP: Record<string, StatusDef> = {
    active: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: 'Active' },
    frozen: { bg: 'bg-blue-100', text: 'text-blue-700', icon: faInfoCircle, label: 'Frozen' },
  };

  const merchantColumns: Column<TransferableMerchant>[] = [
    {
      key: 'select',
      header: '',
      width: '48px',
      render: (m) => (
        <span
          className={`inline-flex w-4 h-4 rounded-full border-2 items-center justify-center ${toMerchant === m.id ? '' : 'border-gray-300 dark:border-slate-600'}`}
          style={toMerchant === m.id ? { borderColor: 'var(--theme-primary)' } : undefined}
        >
          {toMerchant === m.id && <span className="w-2 h-2 rounded-full" style={{ background: 'var(--theme-primary)' }} />}
        </span>
      ),
    },
    {
      key: 'merchant',
      header: t('companyTransfer.toMerchantLabel'),
      render: (m) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
            style={{ background: 'var(--theme-primary-gradient)' }}>
            {m.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{m.name}</p>
            {m.company_name && <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{m.company_name}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'balance',
      header: t('companyTransfer.currentBalance'),
      sortable: true,
      render: (m) => <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">฿{fmt(m.balance)}</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (m) => <StatusBadge status={m.is_frozen ? 'frozen' : 'active'} statusMap={MERCHANT_STATUS_MAP} />,
    },
  ];

  const selectedMerchant = merchants.find(m => m.id === toMerchant);

  return (
    <div className="w-full space-y-6">
      <UiPageHeader
        icon={faArrowRightArrowLeft}
        title={t('companyTransfer.title')}
        subtitle={t('companyTransfer.subtitle')}
      />

      {/* My Balance Card */}
      {myBalance !== null && (
        <div className="rounded-2xl p-5 text-white shadow-lg" style={{ background: 'var(--theme-primary-gradient)' }}>
          <p className="text-white/80 text-sm font-medium">{t('companyTransfer.currentBalance')}</p>
          <p className="text-3xl font-black mt-1">฿{fmt(myBalance)}</p>
          {myName && <p className="text-white/80 text-xs mt-2">{myName}</p>}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-slate-700">
        {[
          { key: 'transfer', icon: faMoneyBillTransfer, label: t('companyTransfer.tabTransfer') },
          { key: 'history',  icon: faHistory,          label: t('companyTransfer.tabHistory') },
        ].map(item => {
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              onClick={() => setTab(item.key as any)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? ''
                  : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
              }`}
              style={active ? { borderColor: 'var(--theme-primary)', color: 'var(--theme-primary)' } : undefined}
            >
              <FontAwesomeIcon icon={item.icon} />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* ===== Tab: Transfer ===== */}
      {tab === 'transfer' && (
        <div className="space-y-5">
          {/* เลือกร้านปลายทางจากตาราง */}
          <DataTable<TransferableMerchant>
            data={merchants}
            columns={merchantColumns}
            loading={loadingMerchants}
            emptyMessage={t('companyTransfer.noMerchantsTitle')}
            emptyIcon={<FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-300 dark:text-slate-600" />}
            keyExtractor={(m) => m.id}
            onRowClick={(m) => { if (m.is_frozen) return; setToMerchant(m.id); setPreview(null); }}
            rowClassName={(m) => `${m.is_frozen ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${toMerchant === m.id ? 'bg-[var(--theme-primary-light)]' : ''}`}
            headerContent={
              <h2 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faBuilding} style={{ color: 'var(--theme-primary)' }} />
                {t('companyTransfer.toMerchantLabel')}
              </h2>
            }
            hoverable
          />

          {/* ฟอร์มจำนวนเงิน — แสดงเมื่อมีร้านให้เลือก */}
          {merchants.length > 0 && (
            <div className="max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-theme border border-gray-100 dark:border-slate-700 p-6 space-y-5">
              {selectedMerchant ? (
                <div className="flex items-center gap-2 text-sm bg-gray-50 dark:bg-slate-700/50 rounded-lg px-3 py-2">
                  <FontAwesomeIcon icon={faBuilding} style={{ color: 'var(--theme-primary)' }} />
                  <span className="text-gray-500 dark:text-slate-400">{t('companyTransfer.toMerchantLabel')}:</span>
                  <span className="font-semibold text-gray-800 dark:text-slate-100 truncate">
                    {selectedMerchant.name}{selectedMerchant.company_name ? ` (${selectedMerchant.company_name})` : ''}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-slate-500">{t('companyTransfer.selectMerchantPlaceholder')}</p>
              )}

              <UiInput
                label={t('companyTransfer.amountLabel')}
                prefix="฿"
                type="number"
                min="1"
                step="0.01"
                value={amount}
                onChange={e => { setAmount(e.target.value); setPreview(null); }}
                placeholder="0.00"
                error={insufficient ? t('companyTransfer.insufficientBalance', { amount: fmt(myBalance as number) }) : undefined}
              />

              <UiInput
                label={t('companyTransfer.descriptionLabel')}
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t('companyTransfer.descriptionPlaceholder')}
              />

              <UiButton
                onClick={handlePreview}
                loading={previewing}
                disabled={!toMerchant || !amount || parseFloat(amount) <= 0}
                icon={faSearch}
                className="w-full justify-center"
              >
                {previewing ? t('companyTransfer.checking') : t('companyTransfer.checkBeforeTransfer')}
              </UiButton>
            </div>
          )}
        </div>
      )}

      {/* ===== Tab: History ===== */}
      {tab === 'history' && (
        <DataTable<Transfer>
          data={transfers}
          columns={historyColumns}
          loading={histLoading}
          emptyMessage={t('companyTransfer.noHistory')}
          emptyIcon={<FontAwesomeIcon icon={faHistory} className="text-5xl text-gray-300 dark:text-slate-600" />}
          keyExtractor={(tx) => tx.id}
          serverPagination={{
            currentPage: Math.floor(pagination.offset / pagination.limit) + 1,
            totalItems: pagination.total,
            pageSize: pagination.limit,
            pageSizeOptions: [pagination.limit],
            onPageChange: (page) => loadHistory((page - 1) * pagination.limit),
          }}
          headerContent={
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faHistory} style={{ color: 'var(--theme-primary)' }} />
                {t('companyTransfer.tabHistory')}
                <span className="ml-1 px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-full text-xs">{t('companyTransfer.historyCount', { count: pagination.total })}</span>
              </h2>
              <button onClick={() => loadHistory(pagination.offset)} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition text-sm">
                <FontAwesomeIcon icon={faSyncAlt} className={histLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          }
          hoverable
        />
      )}

      {/* ===== Confirm Modal ===== */}
      <UiModal
        open={showConfirm && !!preview}
        onClose={() => setShowConfirm(false)}
        title={t('companyTransfer.confirmTitle')}
        icon={faInfoCircle}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowConfirm(false)} className="flex-1 justify-center">
              {t('common.cancel')}
            </UiButton>
            <UiButton
              onClick={handleConfirm}
              loading={submitting}
              disabled={!preview?.sufficient_balance || !!preview?.from_wallet_frozen || !!preview?.to_wallet_frozen}
              icon={faCheckCircle}
              className="flex-1 justify-center"
            >
              {submitting ? t('companyTransfer.transferring') : t('companyTransfer.confirmTransfer')}
            </UiButton>
          </div>
        }
      >
        {preview && (
          <>
            {!preview.sufficient_balance && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-400">
                <FontAwesomeIcon icon={faTimesCircle} /> {t('companyTransfer.insufficientBalanceAlert')}
              </div>
            )}
            {(preview.from_wallet_frozen || preview.to_wallet_frozen) && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-400">
                <FontAwesomeIcon icon={faTimesCircle} />
                {preview.from_wallet_frozen ? t('companyTransfer.sourceFrozen') : t('companyTransfer.destFrozen')}
              </div>
            )}

            <div className="text-center py-2">
              <p className="text-4xl font-black" style={{ color: 'var(--theme-accent)' }}>฿{fmt(preview.amount)}</p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{t('companyTransfer.transferAmount')}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 border border-red-100 dark:border-red-800">
                <p className="text-xs text-red-500 dark:text-red-400 font-semibold mb-1">{t('companyTransfer.sourceLabel')}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">{t('companyTransfer.balanceBefore')} <span className="font-bold text-gray-700 dark:text-slate-200">฿{fmt(preview.from_balance_before)}</span></p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{t('companyTransfer.balanceAfter')} <span className="font-bold text-red-600 dark:text-red-400">฿{fmt(preview.from_balance_after)}</span></p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 border border-emerald-100 dark:border-emerald-800">
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mb-1 truncate">{preview.to_merchant.name}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">{t('companyTransfer.balanceBefore')} <span className="font-bold text-gray-700 dark:text-slate-200">฿{fmt(preview.to_balance_before)}</span></p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{t('companyTransfer.balanceAfter')} <span className="font-bold text-emerald-600 dark:text-emerald-400">฿{fmt(preview.to_balance_after)}</span></p>
              </div>
            </div>

            {description && (
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                <span className="font-semibold text-gray-700 dark:text-slate-200">{t('companyTransfer.noteLabel')}</span> {description}
              </div>
            )}
          </>
        )}
      </UiModal>
    </div>
  );
};

export default CompanyTransferPage;
